import { Plus, LogOut, Search, FolderOpen, Brain, Archive, User, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ActivityInsights } from "@/components/dashboard/ActivityInsights";
import { BoardRecoveryTool } from "@/components/dashboard/BoardRecoveryTool";
import { CompactMomentumIndicator } from "@/components/dashboard/CompactMomentumIndicator";
import { CreateProjectDialog } from "@/components/dashboard/CreateProjectDialog";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { GentleNudgeBanner } from "@/components/dashboard/GentleNudgeBanner";
import { MomentumOverview } from "@/components/dashboard/MomentumOverview";
import { StaleProjectDetector } from "@/components/dashboard/StaleProjectDetector";
import { NotificationCenter } from "@/components/gamification/NotificationCenter";
import { MemoryManager } from "@/components/memories/MemoryManager";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useDashboardState } from "@/hooks/useDashboardState";
import { useUserRole } from "@/hooks/useUserRole";
import { ROUTES } from "@/lib/routes";

const Dashboard = () => {
  const { signOut } = useAuth();
  const { isAdmin } = useUserRole();
  const navigate = useNavigate();

  const {
    workspace,
    boards,
    projects,
    folders,
    tags,
    loading,
    searchQuery,
    emailVerified,
    createProjectDialogOpen,
    isProjectsOpen,
    isBoardsOpen,
    isActivityOpen,
    refetchDashboardData,
    handleResendVerification,
    handleCreateBoard,
    handleCreateProject,
    handleSearch,
    handleSearchQueryChange,
    handleToggleProjects,
    handleToggleBoards,
    handleToggleActivity,
    handleSetCreateProjectDialogOpen,
    handleProjectClick,
    handleBoardClick,
    createProject,
  } = useDashboardState();

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">AdvisorMuse</h1>
            {workspace && <CompactMomentumIndicator workspaceId={workspace.id} />}
            {workspace && (
              <>
                <MemoryManager workspaceId={workspace.id} scopeLabel="workspace" />
                <Button variant="outline" size="sm" onClick={() => navigate("/memories")}>
                  <Brain className="h-4 w-4 mr-2" />
                  All Memories
                </Button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NotificationCenter />
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.PROFILE)}>
              <User className="h-4 w-4 mr-2" />
              Profile
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.TAKEOUT)}>
              <Archive className="h-4 w-4 mr-2" />
              Data Takeout
            </Button>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.ADMIN)}>
                Admin Panel
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={signOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Gentle, non-alarming email verification banner */}
        {!emailVerified && (
          <div className="mb-6 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-blue-600 dark:text-blue-400 text-lg">✉️</div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                  Quick reminder about email verification
                </h3>
                <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                  When you have a moment, verifying your email helps us send you important updates.
                </p>
                <Button
                  onClick={handleResendVerification}
                  variant="outline"
                  size="sm"
                  className="border-blue-400 text-blue-700 hover:bg-blue-100 dark:border-blue-600 dark:text-blue-300 dark:hover:bg-blue-900/40"
                >
                  Resend Verification Email
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Gentle nudge system - shows max 1 nudge per 24h */}
        {workspace && (
          <div className="mb-6">
            <GentleNudgeBanner workspaceId={workspace.id} />
          </div>
        )}

        <div className="mb-8 space-y-4">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => handleSearchQueryChange(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="max-w-md"
            />
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {boards.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">No boards yet</p>
              <Button onClick={handleCreateBoard}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first board
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-12">
            {/* Projects Section */}
            <Collapsible open={isProjectsOpen} onOpenChange={handleToggleProjects}>
              <div className="flex justify-between items-center mb-6">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="p-0 hover:bg-transparent group">
                    <h2 className="text-2xl font-semibold flex items-center gap-2">
                      Your Projects
                      {isProjectsOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      )}
                    </h2>
                  </Button>
                </CollapsibleTrigger>
                <div className="flex gap-2">
                  <Button onClick={() => navigate("/projects")} variant="outline">
                    <FolderOpen className="h-4 w-4 mr-2" />
                    All Projects
                  </Button>
                  <Button onClick={handleCreateProject}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Project
                  </Button>
                </div>
              </div>

              <CollapsibleContent>
                {projects.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <p className="text-muted-foreground mb-2">No projects created yet</p>
                      <p className="text-sm text-muted-foreground">
                        Create your first project to start organizing your work
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {projects.map((project) => (
                      <Card
                        key={project.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => handleProjectClick(project.id)}
                      >
                        <CardHeader>
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="flex-1">{project.name}</CardTitle>
                            {project.boards?.name && (
                              <Badge
                                variant="secondary"
                                className="shrink-0"
                                style={{
                                  backgroundColor: project.boards.color || "#3b82f6",
                                  color: "#ffffff",
                                }}
                              >
                                {project.boards.name}
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>

            {/* Boards Section */}
            <Collapsible open={isBoardsOpen} onOpenChange={handleToggleBoards}>
              <div className="flex justify-between items-center mb-6">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="p-0 hover:bg-transparent group">
                    <h2 className="text-2xl font-semibold flex items-center gap-2">
                      Your Boards
                      {isBoardsOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      )}
                    </h2>
                  </Button>
                </CollapsibleTrigger>
                <Button onClick={handleCreateBoard}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Board
                </Button>
              </div>

              <CollapsibleContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {boards.map((board) => (
                    <Card
                      key={board.id}
                      className="cursor-pointer hover:shadow-md transition-all relative overflow-hidden"
                      onClick={() => handleBoardClick(board.id)}
                    >
                      <div
                        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.08] pointer-events-none"
                        style={{ backgroundColor: board.color }}
                      />
                      <CardHeader className="relative">
                        <CardTitle>{board.name}</CardTitle>
                        <CardDescription>{board.description}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Activity & Insights Section - Collapsed by default for less overwhelm */}
            <Collapsible open={isActivityOpen} onOpenChange={handleToggleActivity} className="mt-12">
              <div className="mb-6">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="p-0 hover:bg-transparent group">
                    <h2 className="text-2xl font-semibold flex items-center gap-2">
                      Activity & Insights
                      {isActivityOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                      )}
                    </h2>
                  </Button>
                </CollapsibleTrigger>
              </div>

              <CollapsibleContent className="space-y-4">
                {/* Momentum Overview moved here - only visible when expanded */}
                {workspace && <MomentumOverview workspaceId={workspace.id} />}

                <BoardRecoveryTool />

                {workspace && (
                  <StaleProjectDetector
                    workspaceId={workspace.id}
                    daysThreshold={28}
                    onProjectsArchived={refetchDashboardData}
                  />
                )}

                {workspace && <ActivityInsights workspaceId={workspace.id} />}
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </main>

      <CreateProjectDialog
        open={createProjectDialogOpen}
        onOpenChange={handleSetCreateProjectDialogOpen}
        boards={boards}
        folders={folders}
        tags={tags}
        createProject={createProject.mutate}
        isCreating={createProject.isPending}
      />
    </div>
  );
};

export default Dashboard;
